// src/types/configuracaoLogin.ts
// Contrato do login parametrizado por tenant. Três rotas, dois públicos distintos:
//
// - GET  /v1/configuracao-login          pública (tenant pelo Host), cache de 60s.
//                                        { permiteSenha, permiteMicrosoft } — tela de login.
// - GET  /v1/configuracao-login/detalhes configuration.listar_login, sem cache.
//                                        { metodoLogin, dataAtualizacao, ... } — painel admin.
// - PUT  /v1/configuracao-login          configuration.editar_login. Devolve o mesmo corpo
//                                        do /detalhes.
//
// A tela de login lê os DOIS booleans; o admin lê e grava UM enum. Os mapeadores abaixo
// são o ÚNICO ponto de tradução entre as duas formas — login e admin nunca divergem.

// Enum espelha o backend EXATAMENTE: [AMBOS, SENHA, MICROSOFT]. Atenção:
// é "AMBOS" (com O) — a issue #28 grafou "AMBAS" por engano; backend é o contrato.
export type MetodoLogin = 'SENHA' | 'MICROSOFT' | 'AMBOS';

export interface ConfiguracaoLogin {
  permiteSenha: boolean;
  permiteMicrosoft: boolean;
}

// Fallback da TELA DE LOGIN quando o GET público falha: mostrar os DOIS métodos. O
// backend recusa o método desabilitado, então o pior caso é uma tentativa negada —
// nunca acesso indevido.
export const CONFIGURACAO_LOGIN_FALLBACK: ConfiguracaoLogin = {
  permiteSenha: true,
  permiteMicrosoft: true,
};

// Checkboxes (booleans) → metodoLogin para o PUT. Os dois desmarcados é estado
// inválido (nenhum método) → null; o chamador bloqueia o save.
export function toMetodoLogin(cfg: ConfiguracaoLogin): MetodoLogin | null {
  const { permiteSenha, permiteMicrosoft } = cfg;
  if (permiteSenha && permiteMicrosoft) return 'AMBOS';
  if (permiteSenha) return 'SENHA';
  if (permiteMicrosoft) return 'MICROSOFT';
  return null;
}

// metodoLogin → checkboxes (booleans). É por aqui que o painel administrativo traduz a
// resposta de /detalhes (e do PUT), mantendo o mapeador como ponto único (ADR-024).
export function fromMetodoLogin(metodo: MetodoLogin): ConfiguracaoLogin {
  return {
    permiteSenha: metodo === 'SENHA' || metodo === 'AMBOS',
    permiteMicrosoft: metodo === 'MICROSOFT' || metodo === 'AMBOS',
  };
}

// Resposta das rotas ADMINISTRATIVAS (GET /detalhes e PUT). A trilha é opcional: sem
// linha na tabela o backend devolve 200 com o método do fail-open e trilha nula — linha
// ausente é anomalia de dado, não configuração.
export interface ConfiguracaoLoginDetalhes {
  metodoLogin: MetodoLogin;
  dataAtualizacao: string | null; // LocalDateTime SEM fuso ("2026-07-25T16:10:00")
}

// Normaliza a resposta administrativa. Dois pontos que não são paranoia:
//
// 1. `atualizadoPorUserId` chega no payload e é DESCARTADO de propósito — é só o ID, e
//    resolver o nome exigiria uma chamada a usuários que quem administra o login pode
//    não ter permissão de fazer.
// 2. A data segue como STRING até a exibição, formatada textualmente por `formatDateTime`
//    (ADR-019). NÃO construir `new Date(...)` com este valor: as datas do backend são
//    timezone-naive, e foi esse `Date` que deslocou todas as datas do sistema em 3h.
export function toConfiguracaoLoginDetalhes(raw: unknown): ConfiguracaoLoginDetalhes {
  const obj = (raw ?? {}) as Partial<Record<keyof ConfiguracaoLoginDetalhes, unknown>>;
  const metodo = obj.metodoLogin;
  const data = obj.dataAtualizacao;
  return {
    metodoLogin:
      metodo === 'SENHA' || metodo === 'MICROSOFT' || metodo === 'AMBOS' ? metodo : 'AMBOS',
    dataAtualizacao: typeof data === 'string' && data.length > 0 ? data : null,
  };
}
